import { z } from "zod";
import { pageImageLinkSchema } from "@/features/salon";

/**
 * The salon's gallery, as `GET /salon/public/gallery` returns it (design note salon-home-landing.md
 * 9.1; TDD-SA FR-SA-195, FR-SA-196). A video is a provider and an id, never an address (BR-SA-039):
 * this app builds the player's address itself, so nothing the salon typed reaches a frame's `src`.
 * An item that fails its check is dropped rather than taking the gallery with it.
 */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const INSTAGRAM_ID = /^[A-Za-z0-9_-]{5,40}$/;

export const galleryVideoSchema = z.discriminatedUnion("provider", [
  z.object({ provider: z.literal("youtube"), videoId: z.string().regex(YOUTUBE_ID) }),
  z.object({ provider: z.literal("instagram"), videoId: z.string().regex(INSTAGRAM_ID) }),
]);

export const galleryItemSchema = z.object({
  id: z.string(),
  kind: z.enum(["image", "video"]),
  image: z.object({ url: pageImageLinkSchema, altText: z.string().default("") }).nullable().default(null),
  video: galleryVideoSchema.nullable().default(null),
  caption: z.string().nullable().default(null),
});

const tolerantItem = galleryItemSchema
  .refine((item) => (item.kind === "image" ? item.image !== null : item.video !== null))
  .nullable()
  .catch(null);

export const gallerySchema = z.object({
  albums: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      items: z
        .array(tolerantItem)
        .transform((items) => items.filter((item): item is GalleryItem => item !== null)),
    }),
  ),
});

export type GalleryItem = z.infer<typeof galleryItemSchema>;
export type GalleryVideo = z.infer<typeof galleryVideoSchema>;
export type Gallery = z.infer<typeof gallerySchema>;

/** The player's address, built here from the provider and id alone; the CSP allows exactly these two. */
export function playerUrl(video: GalleryVideo): string {
  return video.provider === "youtube"
    ? `https://www.youtube-nocookie.com/embed/${video.videoId}?autoplay=1&rel=0`
    : `https://www.instagram.com/reel/${video.videoId}/embed`;
}

/** A YouTube video's still; Instagram offers none without its API, so its tile is drawn instead. */
export function stillUrl(video: GalleryVideo): string | null {
  return video.provider === "youtube" ? `https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg` : null;
}
